/* What the node thought was worth noticing, on one day.
 *
 * These are the node's own verdicts, not this browser's. The classification was
 * made against a rolling baseline computed on the node from far more audio than
 * ever reaches here, so it is reported and never recomputed or second-guessed
 * (R5.3). Where a number is shown it is the node's number.
 *
 * The marks live inside the hourly `features` roll-ups rather than in
 * `kind='events'` objects -- that kind is permitted by the contract and has
 * never been written by anything.
 */
// fmtTime is not used: episodes are shown as a clock range in UTC, matching the
// calendar and timeline, rather than as a localised instant.
import type { Calibration } from '../api'

export interface DetectedEvent {
  t: number
  /** End of the episode, epoch seconds. */
  end_t: number
  /** How many marked minutes it contains. */
  minutes: number
  duration_s: number | null
  verdict: string
  score: number | null
  drivers_hz: number[]
  reason: string | null
  keep_audio: boolean | null
  peak_db: number | null
  sel_db: number | null
  src: string | null
}

/* Two verdicts, and the difference between them is worth stating rather than
 * implying with colour alone. `event` is the node saying something happened;
 * `elevated` is the node saying the background rose without a discrete cause it
 * could point at. Confusing the two turns a busy afternoon into forty alarms. */
const VERDICT: Record<string, { label: string; hint: string }> = {
  event: {
    label: 'Event',
    hint: 'A discrete sound the node scored above its own baseline.',
  },
  elevated: {
    label: 'Elevated',
    hint: 'Background higher than usual, with no single cause identified.',
  },
}

function clock(epoch: number): string {
  const d = new Date(epoch * 1000)
  return `${String(d.getUTCHours()).padStart(2, '0')}:${String(d.getUTCMinutes()).padStart(2, '0')}`
}

function hz(f: number): string {
  return f >= 1000 ? `${(f / 1000).toFixed(1)} kHz` : `${Math.round(f)} Hz`
}

/** The frequencies the node's detector actually keyed on.
 *
 *  This is the closest thing here to "what was it", and it is the field a
 *  client report's "unexpected frequencies" section is built from. Shown as
 *  the node gave them: a boat, a winch and a pile driver do not sound alike and
 *  do not occupy the same bands. */
function Drivers({ list }: { list: number[] }) {
  if (!list?.length) return <span className="faint">—</span>
  return (
    <span className="num">
      {list.slice(0, 3).map(hz).join(' · ')}
      {list.length > 3 && <span className="faint"> +{list.length - 3}</span>}
    </span>
  )
}

export function EventList({
  events, calibration, eventMinutes = 0, observedMinutes = 0, onSeek,
}: {
  events: DetectedEvent[]
  calibration: Calibration
  /** Marked minutes, and minutes observed at all. Shown because an episode
   *  list means nothing without it: forty episodes out of forty minutes and
   *  forty out of a thousand are very different statements, and only one of
   *  them is about the sea. */
  eventMinutes?: number
  observedMinutes?: number
  /** Jump the day view to this moment. */
  onSeek?: (t: number) => void
}) {
  if (!events.length) {
    return (
      <p className="faint" style={{ margin: '4px 2px' }}>
        The node marked nothing on this day. That is a statement about the sea,
        not a gap in the data — it reports quiet as readily as it reports a
        vessel.
      </p>
    )
  }

  // The unit is the server's judgement, never arithmetic done here (R5.3).
  const unit = calibration.absolute ? 'dB re 1 µPa' : 'dBFS'
  const share = observedMinutes ? eventMinutes / observedMinutes : 0
  const mains = events.some((e) => e.drivers_hz?.some((f) => f >= 47 && f <= 53))

  return (
    <>
      {/* A detector that fires almost continuously is not describing the sea,
          and a list that does not say so invites its output to be believed.
          Stated before the table, because by the time somebody has read three
          rows they have already formed an impression. */}
      {share > 0.5 && (
        <div className="notice bad" style={{ marginBottom: 10 }}>
          <strong>
            {Math.round(share * 100)}% of the observed minutes are marked.
          </strong>{' '}
          At that rate the detector is describing a condition rather than
          picking out moments, so treat the episodes below as one finding and
          not as {events.length === 1 ? 'an event' : `${events.length} events`}.
          {mains && (
            <>
              {' '}The dominant frequencies are around 50 Hz, which is mains
              hum rather than anything in the water — this recorder is
              almost certainly picking up its own power supply.
            </>
          )}
        </div>
      )}
      <div className="scroll-x">
        <table className="tbl">
          <thead>
            <tr>
              <th>When</th>
              <th>What</th>
              <th className="r">For</th>
              <th className="r">Peak</th>
              <th className="r">Score</th>
              <th>Frequencies</th>
              <th>Audio</th>
            </tr>
          </thead>
          <tbody>
            {events.map((e) => {
              const v = VERDICT[e.verdict] ?? { label: e.verdict, hint: '' }
              return (
                <tr key={`${e.t}-${e.verdict}`}>
                  <td data-l="When">
                    {onSeek ? (
                      <button
                        className="rowlink"
                        style={{ background: 'none', border: 0, padding: 0, cursor: 'pointer' }}
                        onClick={() => onSeek(e.t)}
                      >
                        {clock(e.t)}–{clock(e.end_t)}
                      </button>
                    ) : <>{clock(e.t)}–{clock(e.end_t)}</>}
                  </td>
                  <td data-l="What">
                    <span className={`badge${e.verdict === 'event' ? ' alert' : ''}`}
                          title={v.hint}>
                      {v.label}
                    </span>
                  </td>
                  <td data-l="For" className="r num">{e.minutes} min</td>
                  <td data-l="Peak" className="r num">
                    {e.peak_db != null ? e.peak_db.toFixed(1) : '—'}
                  </td>
                  <td data-l="Score" className="r num">
                    {e.score != null ? e.score.toFixed(2) : '—'}
                  </td>
                  <td data-l="Frequencies"><Drivers list={e.drivers_hz} /></td>
                  <td data-l="Audio">
                    {/* The node's own statement about whether it kept the
                        audio. It is what makes "can I listen to this?"
                        answerable without going to look for a clip that was
                        never written -- and on an events-tier node the answer
                        is on the card, not in the cloud. */}
                    {e.keep_audio === true ? (
                      <span className="faint">kept on the node</span>
                    ) : e.keep_audio === false ? (
                      <span className="faint">not kept</span>
                    ) : <span className="faint">—</span>}
                  </td>
                </tr>
              )
            })}
          </tbody>
        </table>
      </div>

      <p className="faint" style={{ fontSize: 12, margin: '8px 2px 0' }}>
        {observedMinutes > 0 && (
          <>{eventMinutes} of {observedMinutes} observed minutes marked, grouped
          into {events.length} episode{events.length === 1 ? '' : 's'}. </>
        )}
        Peaks are {unit}
        {!calibration.absolute && ', relative to this recorder and comparable' +
          ' only with itself'}
        . Verdicts and scores are the node's own, made against a baseline
        computed from more audio than reaches this server.
      </p>
    </>
  )
}
